// Prompt 0456 — Hook Tiptap comun para las secciones de ficha (puesto + rol funcional).
// Extraido de puesto-panel/tabs/FichaTab.tsx y rol-funcional-panel/tabs/FichaTab.tsx
// (0454 + 0455). Centraliza el set de extensiones que antes estaba duplicado.
//
// Contrato:
//   - Recibe los nodos iniciales (array ProseMirror) y callbacks onUpdate/onFocus.
//   - Inicializa un editor Tiptap con las extensiones consolidadas:
//       StarterKit + Underline + TextAlign + TextStyle + Color.
//   - Devuelve el Editor (o null mientras Tiptap lo inicializa).
//
// Nota: el hook NO memoriza el contenido inicial por cambios. Es responsabilidad
// del consumidor pasar `contenidoInicialKey` estable (p.ej. seccionId) para que
// React evalue las dependencias correctamente — mismo patron que la version
// anterior, se mantiene para no cambiar el comportamiento.

import { useMemo } from 'react';
import { useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Underline } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import type { ProseMirrorNode } from './types';

export interface UseFichaEditorParams {
  nodosIniciales: ProseMirrorNode[];
  onUpdate: (nodos: ProseMirrorNode[]) => void;
  onFocus?: () => void;
  editable?: boolean;
}

export function useFichaEditor({
  nodosIniciales,
  onUpdate,
  onFocus,
  editable = true,
}: UseFichaEditorParams): Editor | null {
  const initialContent = useMemo(
    () => ({
      type: 'doc',
      content: nodosIniciales.length > 0 ? nodosIniciales : [{ type: 'paragraph' }],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const editor: Editor | null = useEditor(
    {
      extensions: [
        StarterKit,
        Underline,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        TextStyle,
        Color,
      ],
      content: initialContent,
      editable,
      onUpdate: ({ editor: ed }) => {
        const json = ed.getJSON();
        const content = Array.isArray(json.content) ? (json.content as ProseMirrorNode[]) : [];
        onUpdate(content);
      },
      onFocus: () => onFocus?.(),
    },
    [],
  );

  return editor;
}
